// HTTP transport client for wiser-accounts. The one client office/mail/vault import for sign-in/up.
// Crypto (deriving the auth verifier, generating + wrapping keys) is composed by the caller via @wiser/e2e.
import type {
  AccountsApi, AccountBlobs, SignupInput, SignupResult, LoginResult, LookupResult, RecoverResult,
  ChangePasswordInput, SetupRecoveryInput, RecoverResetInput, RecoveryStatusResult, ChangesResult,
  VerifyEmailResult, SessionInfo, RevokeSessionsInput, ChangeEmailInput, DeleteAccountInput,
  NewDeviceChallenge, DeviceChallengeInput, RefreshResult, AuditEntry, AuditFilters,
} from './types';

/** Thrown by `login` when the account exists and the password is correct but the email has not been verified
 *  yet (server replies 403 `{ error: "email_not_verified", email }`). Carries the `email` so the UI can offer
 *  a "resend verification" affordance. Distinguishable from a generic auth failure via `instanceof`. */
export class EmailNotVerifiedError extends Error {
  readonly email: string;
  constructor(email: string) {
    super('email_not_verified');
    this.name = 'EmailNotVerifiedError';
    this.email = email;
    // Restore the prototype chain (TS targets below ES2015 / transpiled extends break instanceof otherwise).
    Object.setPrototypeOf(this, EmailNotVerifiedError.prototype);
  }
}

export interface AccountsClientOptions {
  /** Base URL of the wiser-accounts service, e.g. http://wiser-accounts.lan:8097 */
  baseUrl: string;
  /** Injectable fetch (tests / non-browser). Defaults to global fetch. */
  fetch?: typeof fetch;
}

export class AccountsClient implements AccountsApi {
  private readonly base: string;
  private readonly f: typeof fetch;

  constructor(opts: AccountsClientOptions) {
    this.base = opts.baseUrl.replace(/\/+$/, '');
    // Bind to the global: a bare `fetch` reference invoked as `this.f(...)` runs with `this === client`,
    // which browsers reject with "Illegal invocation". Binding to globalThis keeps native fetch's receiver.
    this.f = opts.fetch ?? fetch.bind(globalThis);
  }

  private async json<T>(path: string, init?: RequestInit): Promise<T> {
    const res = await this.f(this.base + path, init);
    if (!res.ok) {
      const body = await res.text().catch(() => '');
      throw new Error(`wiser-accounts ${init?.method ?? 'GET'} ${path} → ${res.status}: ${body}`);
    }
    return res.json() as Promise<T>;
  }

  private post<T>(path: string, body: unknown, headers: HeadersInit = {}): Promise<T> {
    return this.json<T>(path, {
      method: 'POST', headers: { 'content-type': 'application/json', ...headers }, body: JSON.stringify(body),
    });
  }

  signup(input: SignupInput): Promise<SignupResult> {
    return this.post<SignupResult>('/accounts/signup', input);
  }

  lookup(email: string): Promise<LookupResult> {
    return this.json<LookupResult>(`/accounts/lookup?email=${encodeURIComponent(email)}`);
  }

  async login(email: string, authVerifierB64: string, deviceId?: string, issueRefresh?: boolean, client?: string): Promise<LoginResult | NewDeviceChallenge> {
    // Hand-rolled (rather than via `post`) so we can intercept the 403 email_not_verified case and surface a
    // typed error instead of the generic `Error` the `json` helper throws for non-2xx.
    // `client` (0022) is audit provenance only — the dashboard/app performing the login (e.g. "selfserve",
    // "console"). Omitted when undefined so it stays off the wire for older callers.
    const res = await this.f(this.base + '/accounts/login', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        email,
        auth_verifier_b64: authVerifierB64,
        device_id: deviceId,
        issue_refresh: issueRefresh,
        ...(client !== undefined ? { client } : {}),
      }),
    });
    if (res.status === 403) {
      const body = await res.json().catch(() => null) as { error?: string; email?: string } | null;
      if (body?.error === 'email_not_verified') throw new EmailNotVerifiedError(body.email ?? email);
    }
    if (!res.ok) {
      const body = await res.text().catch(() => '');
      throw new Error(`wiser-accounts POST /accounts/login → ${res.status}: ${body}`);
    }
    // A gated account on an unknown device returns { challenge: 'new_device', email } instead of a session.
    return res.json() as Promise<LoginResult | NewDeviceChallenge>;
  }

  async deviceChallenge(input: DeviceChallengeInput): Promise<LoginResult> {
    const res = await this.f(this.base + '/accounts/device-challenge', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(input),
    });
    if (res.status === 403) {
      const body = await res.json().catch(() => null) as { error?: string; email?: string } | null;
      if (body?.error === 'email_not_verified') throw new EmailNotVerifiedError(body.email ?? input.email);
    }
    if (!res.ok) {
      const body = await res.text().catch(() => '');
      throw new Error(`wiser-accounts POST /accounts/device-challenge → ${res.status}: ${body}`);
    }
    return res.json() as Promise<LoginResult>;
  }

  async refreshSession(refreshToken: string): Promise<RefreshResult> {
    // A revoked/expired/unknown token is a 401 — surfaced as the generic Error the caller treats as
    // "re-login + re-verify this device".
    return this.post<RefreshResult>('/accounts/refresh', { refresh_token: refreshToken });
  }

  async revokeRefresh(refreshToken: string): Promise<void> {
    // Server returns { ok: true }; consumed and discarded (the API contract is void). Idempotent.
    await this.post<unknown>('/accounts/refresh/revoke', { refresh_token: refreshToken });
  }

  verifyEmail(token: string): Promise<VerifyEmailResult> {
    return this.post<VerifyEmailResult>('/accounts/verify-email', { token });
  }

  async resendVerification(email: string): Promise<void> {
    // Server returns { ok: true } regardless (rate-limited); consumed and discarded.
    await this.post<unknown>('/accounts/verify-email/resend', { email });
  }

  me(sessionToken: string): Promise<AccountBlobs> {
    return this.json<AccountBlobs>('/accounts/me', { headers: { authorization: `Bearer ${sessionToken}` } });
  }

  async logout(sessionToken: string): Promise<void> {
    // Server returns { ok: true }; consumed and discarded (the API contract is void).
    await this.post<unknown>('/accounts/logout', undefined, { authorization: `Bearer ${sessionToken}` });
  }

  async changePassword(input: ChangePasswordInput): Promise<void> {
    await this.post<unknown>('/accounts/change-password', input);
  }

  listSessions(sessionToken: string): Promise<SessionInfo[]> {
    return this.json<SessionInfo[]>('/accounts/sessions', { headers: { authorization: `Bearer ${sessionToken}` } });
  }

  async revokeSessions(sessionToken: string, input: RevokeSessionsInput): Promise<void> {
    await this.post<unknown>('/accounts/sessions/revoke', input, { authorization: `Bearer ${sessionToken}` });
  }

  async changeEmailRequest(sessionToken: string, newEmail: string): Promise<void> {
    await this.post<unknown>('/accounts/change-email-request', { new_email: newEmail },
      { authorization: `Bearer ${sessionToken}` });
  }

  changeEmail(sessionToken: string, input: ChangeEmailInput): Promise<AccountBlobs> {
    return this.post<AccountBlobs>('/accounts/change-email', input, { authorization: `Bearer ${sessionToken}` });
  }

  async deleteAccount(sessionToken: string, input: DeleteAccountInput): Promise<void> {
    await this.post<unknown>('/accounts/delete', input, { authorization: `Bearer ${sessionToken}` });
  }

  recoveryStatus(email: string): Promise<RecoveryStatusResult> {
    return this.json<RecoveryStatusResult>(`/accounts/recovery-status?email=${encodeURIComponent(email)}`);
  }

  async setupRecovery(input: SetupRecoveryInput): Promise<void> {
    await this.post<unknown>('/accounts/recovery', input);
  }

  recover(email: string, recoveryVerifierB64: string): Promise<RecoverResult> {
    return this.post<RecoverResult>('/accounts/recover', { email, recovery_verifier_b64: recoveryVerifierB64 });
  }

  /** Begin a forgot-password reset: email a one-time 10-digit PIN to the account. Always resolves (constant
   *  shape, not an existence oracle); the PIN is then required by recoverReset alongside the recovery key. */
  async resetRequest(email: string): Promise<void> {
    await this.post<unknown>('/accounts/reset-request', { email });
  }

  async recoverReset(input: RecoverResetInput): Promise<void> {
    await this.post<unknown>('/accounts/recover-reset', input);
  }

  changes(since: number, limit?: number, readToken?: string): Promise<ChangesResult> {
    const params = new URLSearchParams({ since: String(since) });
    if (limit != null) params.set('limit', String(limit));
    const headers: HeadersInit = readToken ? { authorization: `Bearer ${readToken}` } : {};
    return this.json<ChangesResult>(`/accounts/changes?${params.toString()}`, { headers });
  }

  /** Read one of the signed-in user's per-key UI prefs (0022). Returns the stored JSON value, or `null` when
   *  unset. The server answers `{ value: <json>|null }`; we unwrap to the value the caller cares about. */
  async getPref<T>(sessionToken: string, key: string): Promise<T | null> {
    const { value } = await this.json<{ value: T | null }>(
      `/me/prefs/${encodeURIComponent(key)}`,
      { headers: { authorization: `Bearer ${sessionToken}` } },
    );
    return value;
  }

  /** Upsert one of the signed-in user's per-key UI prefs (0022). Server returns `{ ok: true }`; consumed and
   *  discarded (the API contract is void). Throws the generic Error on a 400 (bad key / oversize value). */
  async setPref(sessionToken: string, key: string, value: unknown): Promise<void> {
    await this.json<unknown>(`/me/prefs/${encodeURIComponent(key)}`, {
      method: 'PUT',
      headers: { 'content-type': 'application/json', authorization: `Bearer ${sessionToken}` },
      body: JSON.stringify({ value }),
    });
  }

  /** Serialize audit filters to a `?...` query string (empty string when there's nothing to constrain). */
  private auditQuery(filters?: AuditFilters): string {
    if (!filters) return '';
    const p = new URLSearchParams();
    if (filters.action) p.set('action', filters.action);
    if (filters.actor) p.set('actor', filters.actor);
    if (filters.since) p.set('since', filters.since);
    if (filters.until) p.set('until', filters.until);
    if (filters.limit !== undefined) p.set('limit', String(filters.limit));
    const q = p.toString();
    return q ? `?${q}` : '';
  }

  /** `GET /org/audit` — the caller's ORG audit trail (tenant-scoped), newest-first, optionally filtered.
   *  Owner/admin session only. Unwraps `{ entries }` to the `AuditEntry[]` the <AuditLog> table consumes. */
  async orgAudit(sessionToken: string, filters?: AuditFilters): Promise<AuditEntry[]> {
    const { entries } = await this.json<{ entries: AuditEntry[] }>(
      `/org/audit${this.auditQuery(filters)}`,
      { headers: { authorization: `Bearer ${sessionToken}` } },
    );
    return entries;
  }

  /** `GET /org/audit/{id}` — one org audit entry by id. Rejects (404 → generic Error) if the entry doesn't
   *  belong to the caller's org, so an owner can't read another org's entry. */
  async orgAuditEntry(sessionToken: string, id: string): Promise<AuditEntry> {
    return this.json<AuditEntry>(
      `/org/audit/${encodeURIComponent(id)}`,
      { headers: { authorization: `Bearer ${sessionToken}` } },
    );
  }

  /** `GET /admin/audit` — the GLOBAL audit trail (console; admin-gated), newest-first, optionally filtered. */
  async adminAudit(sessionToken: string, filters?: AuditFilters): Promise<AuditEntry[]> {
    const { entries } = await this.json<{ entries: AuditEntry[] }>(
      `/admin/audit${this.auditQuery(filters)}`,
      { headers: { authorization: `Bearer ${sessionToken}` } },
    );
    return entries;
  }

  /** `GET /admin/audit/{id}` — one audit entry by id (any row; admin-gated). */
  async adminAuditEntry(sessionToken: string, id: string): Promise<AuditEntry> {
    return this.json<AuditEntry>(
      `/admin/audit/${encodeURIComponent(id)}`,
      { headers: { authorization: `Bearer ${sessionToken}` } },
    );
  }
}
